import React from 'react';
import i18next, { i18n as I18nInstance } from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import enCommon from '../locales/en/common.json';
import roCommon from '../locales/ro/common.json';
import ruCommon from '../locales/ru/common.json';
import { I18nService } from '../api/services/I18nService';
import { UserService } from '../api/services/UserService';
import { loadCachedCatalog, saveCachedCatalog } from './cache';
import { I18nLocaleMetadata, I18nLocalesResponse, SupportedLocale } from './types';

const LOCALE_STORAGE_KEY = 'gym_planner_locale';

const bootstrapResources = {
  en: { common: enCommon },
  ro: { common: roCommon },
  ru: { common: ruCommon },
};

const FALLBACK_LOCALES: I18nLocaleMetadata[] = [
  { code: 'en', label: 'English', nativeLabel: 'English', rtl: false, version: 'bootstrap', namespaces: ['common'] },
  { code: 'ro', label: 'Romanian', nativeLabel: 'Romana', rtl: false, version: 'bootstrap', namespaces: ['common'] },
  { code: 'ru', label: 'Russian', nativeLabel: 'Russkiy', rtl: false, version: 'bootstrap', namespaces: ['common'] },
];

const i18n: I18nInstance = i18next.createInstance();
let initPromise: Promise<void> | null = null;

const ensureI18nInitialized = (initialLocale: SupportedLocale): Promise<void> => {
  if (i18n.isInitialized) {
    return Promise.resolve();
  }

  if (!initPromise) {
    initPromise = i18n
      .use(initReactI18next)
      .init({
        resources: bootstrapResources,
        lng: initialLocale,
        fallbackLng: 'en',
        defaultNS: 'common',
        ns: ['common'],
        interpolation: {
          escapeValue: false,
        },
        react: {
          useSuspense: false,
        },
      })
      .then(() => undefined);
  }

  return initPromise;
};

const normalizeLocale = (value?: string | null): SupportedLocale => {
  const normalized = value?.toLowerCase().trim();
  if (!normalized) {
    return 'en';
  }
  return normalized;
};

const loadStoredLocale = (): SupportedLocale => {
  try {
    return normalizeLocale(localStorage.getItem(LOCALE_STORAGE_KEY));
  } catch {
    return 'en';
  }
};

const hasAuthToken = (): boolean => {
  try {
    return !!localStorage.getItem('gym_planner_token');
  } catch {
    return false;
  }
};

const updateStoredUserPreferredLocale = (locale: SupportedLocale): void => {
  try {
    const rawUser = localStorage.getItem('gym_planner_user');
    if (!rawUser) {
      return;
    }
    const parsed = JSON.parse(rawUser) as Record<string, unknown>;
    parsed.preferredLocale = locale;
    localStorage.setItem('gym_planner_user', JSON.stringify(parsed));
  } catch {
    // Ignore localStorage parse/write failures.
  }
};

const persistLocale = (locale: SupportedLocale): void => {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Ignore storage failures.
  }
};

// Ensure react-i18next has an initialized instance before any component hook calls.
void ensureI18nInitialized(loadStoredLocale());

const applyNamespaces = (
  language: SupportedLocale,
  namespaces: Record<string, Record<string, unknown>>
): void => {
  for (const [namespace, payload] of Object.entries(namespaces)) {
    i18n.addResourceBundle(language, namespace, payload, true, true);
  }
};

interface I18nContextType {
  locale: SupportedLocale;
  availableLocales: I18nLocaleMetadata[];
  isLoading: boolean;
  error: string | null;
  setLocale: (nextLocale: SupportedLocale) => Promise<void>;
}

const I18nContext = React.createContext<I18nContextType | undefined>(undefined);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const service = React.useMemo(() => new I18nService(), []);
  const userService = React.useMemo(() => new UserService(), []);
  const [locale, setLocaleState] = React.useState<SupportedLocale>(() => loadStoredLocale());
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const [metadata, setMetadata] = React.useState<I18nLocalesResponse | null>(null);

  const availableLocales = metadata?.locales ?? FALLBACK_LOCALES;

  const setDocumentLanguage = React.useCallback((nextLocale: SupportedLocale) => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = nextLocale;
    }
  }, []);

  const resolveLocale = React.useCallback(
    (requested: SupportedLocale, metadataOverride?: I18nLocalesResponse | null): SupportedLocale => {
      const sourceLocales = metadataOverride?.locales ?? availableLocales;
      const exists = sourceLocales.some((entry) => entry.code === requested);
      if (exists) {
        return requested;
      }
      return normalizeLocale(metadataOverride?.defaultLocale || metadata?.defaultLocale || 'en');
    },
    [availableLocales, metadata?.defaultLocale]
  );

  const loadAndApplyLocale = React.useCallback(
    async (requestedLocale: SupportedLocale, metadataOverride?: I18nLocalesResponse | null): Promise<void> => {
      const sourceMetadata = metadataOverride ?? metadata;
      const sourceLocales = sourceMetadata?.locales ?? availableLocales;

      const resolvedLocale = resolveLocale(requestedLocale, sourceMetadata);
      const localeMeta = sourceLocales.find((entry) => entry.code === resolvedLocale);
      const namespaces = localeMeta?.namespaces ?? ['common'];
      const expectedVersion = sourceMetadata?.catalogVersionMap?.[resolvedLocale] ?? localeMeta?.version;

      const cached = loadCachedCatalog(resolvedLocale);
      const canUseCached = !!cached && (!!expectedVersion ? cached.version === expectedVersion : true);

      if (canUseCached && cached) {
        applyNamespaces(resolvedLocale, cached.namespaces);
      }

      try {
        const result = await service.getCatalog(resolvedLocale, namespaces, cached?.etag);

        if (result.status === 304 && cached) {
          applyNamespaces(resolvedLocale, cached.namespaces);
        }

        if (result.data) {
          applyNamespaces(resolvedLocale, result.data.namespaces);
          saveCachedCatalog({
            locale: resolvedLocale,
            version: result.data.version,
            etag: result.etag || cached?.etag || '',
            namespaces: result.data.namespaces,
            cachedAt: new Date().toISOString(),
          });
        }
      } catch (catalogError) {
        if (!canUseCached) {
          console.warn('Failed to fetch i18n catalog and no valid cache found:', catalogError);
          setError('Unable to load translation catalog. Using fallback language.');
        }
      }

      await i18n.changeLanguage(resolvedLocale);
      setDocumentLanguage(resolvedLocale);
      setLocaleState(resolvedLocale);
      persistLocale(resolvedLocale);
    },
    [availableLocales, metadata?.catalogVersionMap, resolveLocale, service, setDocumentLanguage]
  );

  React.useEffect(() => {
    let active = true;

    const bootstrap = async () => {
      setIsLoading(true);
      setError(null);

      let initialLocale = loadStoredLocale();

      if (hasAuthToken()) {
        try {
          const currentUser = await userService.getCurrentUser();
          if (currentUser.preferredLocale) {
            initialLocale = normalizeLocale(currentUser.preferredLocale);
            persistLocale(initialLocale);
            updateStoredUserPreferredLocale(initialLocale);
          }
        } catch (userError) {
          console.warn('Failed to restore preferred locale from user profile:', userError);
        }
      }

      await ensureI18nInitialized(initialLocale);

      let fetchedMetadata: I18nLocalesResponse | null = null;

      try {
        const localesMetadata = await service.getLocalesMetadata();
        fetchedMetadata = localesMetadata;
        if (active) {
          setMetadata(localesMetadata);
        }
      } catch (metadataError: any) {
        console.warn('Failed to load locale metadata, using fallback locales:', metadataError);
        if (active) {
          setError(metadataError?.message || 'Failed to load locale metadata');
        }
      }

      if (active) {
        await loadAndApplyLocale(initialLocale, fetchedMetadata);
        setIsLoading(false);
      }
    };

    void bootstrap();

    return () => {
      active = false;
    };
  }, [service, userService]);

  const contextValue = React.useMemo<I18nContextType>(
    () => ({
      locale,
      availableLocales,
      isLoading,
      error,
      setLocale: async (nextLocale: SupportedLocale) => {
        setIsLoading(true);
        setError(null);
        try {
          await loadAndApplyLocale(nextLocale);
          if (hasAuthToken()) {
            try {
              await userService.updatePreferredLocale(nextLocale);
              updateStoredUserPreferredLocale(nextLocale);
            } catch (persistError) {
              console.warn('Failed to persist preferred locale for authenticated user:', persistError);
              setError('Language changed locally, but failed to save locale preference to your profile.');
            }
          }
        } catch (localeError: any) {
          setError(localeError?.message || 'Failed to switch locale');
        } finally {
          setIsLoading(false);
        }
      },
    }),
    [availableLocales, error, isLoading, loadAndApplyLocale, locale, userService]
  );

  return <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18nContextType => {
  const ctx = React.useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return ctx;
};

export { useTranslation };
